import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ExternalLink,
  Globe,
  Building2,
  Calendar,
  MapPin,
  Tag,
  ClipboardList,
  Award,
  Shield,
  ChevronRight,
  Pencil,
  UserPlus,
  Loader2,
  X,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  RotateCcw,
  RefreshCw,
  Archive,
  History,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { VendorBiHeader } from '@/components/assessment/VendorBiHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { rrBadgeStatus } from '@/components/risk-acceptances/rrBadgeStatus';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  useVendor,
  useVendorContacts,
  useVendorAssessments,
  useVendorCertifications,
  useVendorRiskAcceptances,
  useUpdateVendor,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
  useReviewCertification,
  useVendorLifecycleEvents,
} from '@/api/hooks/useVendors';
import { VendorRetireModal } from './VendorRetireModal';
import { VendorReactivateModal } from './VendorReactivateModal';
import { VendorLifecycleHistory } from './VendorLifecycleHistory';
import { formatDate } from '@/lib/utils';
import { TIER_LABELS } from '@/api/types/vendor.types';
import { PortalUsersTab } from '@/components/tprm/vendor-detail/PortalUsersTab';
import { CrmContactsTab } from '@/components/tprm/vendor-detail/CrmContactsTab';
import { AssessmentStatusChip } from '@/components/vendor-assessments/AssessmentStatusChip';
import { ACTIVE_STATUSES } from '@/api/types/assessment.types';
import type { Certification } from '@/api/types/assessment.types';
import type { Vendor } from '@/api/types/vendor.types';
import { useAuthStore } from '@/store/auth.store';
import { useRiskAcceptanceAction } from '@/api/hooks/useRiskAcceptances';
import { RiskAcceptanceActionModal } from '@/components/shared/RiskAcceptanceActionModal';

// ── Score breakdown ────────────────────────────────────────────────────────────

// ── Key Contact inline-edit components ────────────────────────────────────────

function ContactRow({ contact, vendorId }: { contact: import('@/api/types/vendor.types').VendorContact; vendorId: string }) {
  const update = useUpdateContact(vendorId);
  const remove = useDeleteContact(vendorId);
  const [fields, setFields] = useState({
    firstName: contact.first_name,
    lastName: contact.last_name,
    email: contact.email,
    title: contact.title ?? '',
  });
  const [confirmDelete, setConfirmDelete] = useState(false);

  function handleBlur(key: 'firstName' | 'lastName' | 'email' | 'title') {
    const value = fields[key];
    const orig = ({ firstName: contact.first_name, lastName: contact.last_name, email: contact.email, title: contact.title ?? '' })[key];
    if (value === orig || update.isPending) return;
    update.mutate({ contactId: contact.id, [key]: value || undefined });
  }

  return (
    <div className="relative rounded-lg border border-border-subtle p-2.5 space-y-2">
      {/* Remove button */}
      {confirmDelete ? (
        <div className="absolute top-2 right-2 flex items-center gap-1.5 text-xs">
          <span className="text-text-secondary">Remove?</span>
          <button type="button" className="text-danger hover:underline font-medium" onClick={() => remove.mutate(contact.id)}>
            {remove.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes'}
          </button>
          <button type="button" className="text-text-secondary hover:underline" onClick={() => setConfirmDelete(false)}>No</button>
        </div>
      ) : (
        <button type="button" className="absolute top-2 right-2 text-text-secondary hover:text-danger" onClick={() => setConfirmDelete(true)}>
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      <div className="grid grid-cols-2 gap-2 pr-16">
        <Input
          value={fields.firstName}
          onChange={(e) => setFields((p) => ({ ...p, firstName: e.target.value }))}
          onBlur={() => handleBlur('firstName')}
          placeholder="First name"
          className="h-7 text-xs"
        />
        <Input
          value={fields.lastName}
          onChange={(e) => setFields((p) => ({ ...p, lastName: e.target.value }))}
          onBlur={() => handleBlur('lastName')}
          placeholder="Last name"
          className="h-7 text-xs"
        />
      </div>
      <div className="grid grid-cols-2 gap-2 pr-16">
        <Input
          value={fields.email}
          onChange={(e) => setFields((p) => ({ ...p, email: e.target.value }))}
          onBlur={() => handleBlur('email')}
          placeholder="Email"
          type="email"
          className="h-7 text-xs"
        />
        <Input
          value={fields.title}
          onChange={(e) => setFields((p) => ({ ...p, title: e.target.value }))}
          onBlur={() => handleBlur('title')}
          placeholder="Title / Role"
          className="h-7 text-xs"
        />
      </div>

      {contact.is_primary && (
        <Badge variant="emerald" className="text-[10px] py-0 px-1.5">Primary</Badge>
      )}
    </div>
  );
}

function NewContactRow({ vendorId, onDone }: { vendorId: string; onDone: () => void }) {
  const create = useCreateContact(vendorId);
  const [fields, setFields] = useState({ firstName: '', lastName: '', email: '', title: '' });

  async function handleAdd() {
    if (!fields.firstName || !fields.lastName || !fields.email) return;
    await create.mutateAsync({
      firstName: fields.firstName,
      lastName: fields.lastName,
      email: fields.email,
      title: fields.title || undefined,
    });
    onDone();
  }

  return (
    <div className="rounded-lg border border-accent-cyan/40 bg-accent-cyan/5 p-2.5 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Input value={fields.firstName} onChange={(e) => setFields((p) => ({ ...p, firstName: e.target.value }))} placeholder="First name *" className="h-7 text-xs" autoFocus />
        <Input value={fields.lastName} onChange={(e) => setFields((p) => ({ ...p, lastName: e.target.value }))} placeholder="Last name *" className="h-7 text-xs" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input value={fields.email} onChange={(e) => setFields((p) => ({ ...p, email: e.target.value }))} placeholder="Email *" type="email" className="h-7 text-xs" />
        <Input value={fields.title} onChange={(e) => setFields((p) => ({ ...p, title: e.target.value }))} placeholder="Title / Role" className="h-7 text-xs" />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-7 text-xs px-3"
          disabled={create.isPending || !fields.firstName || !fields.lastName || !fields.email}
          onClick={handleAdd}
        >
          {create.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Add Contact
        </Button>
        <button type="button" className="text-xs text-text-secondary hover:text-text-primary" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function KeyContactsSection({ vendorId }: { vendorId: string }) {
  const { data: contacts = [], isLoading } = useVendorContacts(vendorId);
  const [addingNew, setAddingNew] = useState(false);

  return (
    <div className="space-y-3 pt-4 border-t border-border-subtle">
      <div>
        <p className="text-sm font-semibold text-text-primary">Key Contacts</p>
        <p className="text-xs text-text-secondary mt-0.5">
          Internal reference contacts for your team. To grant portal access, use the Vendor Contacts tab.
        </p>
      </div>

      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : contacts.length === 0 && !addingNew ? (
        <p className="text-xs text-text-secondary">No key contacts added yet.</p>
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => (
            <ContactRow key={c.id} contact={c} vendorId={vendorId} />
          ))}
          {addingNew && (
            <NewContactRow vendorId={vendorId} onDone={() => setAddingNew(false)} />
          )}
        </div>
      )}

      {!addingNew && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs gap-1"
          onClick={() => setAddingNew(true)}
        >
          <UserPlus className="h-3.5 w-3.5" />
          Add Contact
        </Button>
      )}
    </div>
  );
}

// ── Edit Vendor Modal ─────────────────────────────────────────────────────────

function EditVendorModal({
  vendor,
  open,
  onClose,
}: {
  vendor: Vendor;
  open: boolean;
  onClose: () => void;
}) {
  const update = useUpdateVendor(vendor.id);

  const [form, setForm] = useState({
    name: vendor.name,
    tier: vendor.tier,
    industry: vendor.industry ?? '',
    website: vendor.website ?? '',
    country: vendor.country ?? '',
    description: vendor.description ?? '',
    contract_start_date: vendor.contract_start_date?.slice(0, 10) ?? '',
    contract_end_date: vendor.contract_end_date?.slice(0, 10) ?? '',
    data_access_level: vendor.data_access_level ?? 'none',
    services_provided: (vendor.services_provided ?? []).join(', '),
  });

  function set(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await update.mutateAsync({
      name: form.name.trim(),
      tier: form.tier as Vendor['tier'],
      industry: form.industry.trim() || undefined,
      website: form.website.trim() || undefined,
      country: form.country.trim() || undefined,
      description: form.description.trim() || undefined,
      contract_start_date: form.contract_start_date || undefined,
      contract_end_date: form.contract_end_date || undefined,
      data_access_level: form.data_access_level,
      services_provided: form.services_provided
        ? form.services_provided.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
    });
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Vendor Profile</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-2">
          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-name">Name <span className="text-danger">*</span></Label>
            <Input
              id="ev-name"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
            />
          </div>

          {/* Tier */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-tier">Tier</Label>
            <select
              id="ev-tier"
              value={form.tier}
              onChange={(e) => set('tier', e.target.value)}
              className="flex h-9 w-full rounded border border-border-subtle bg-bg-surface-2 px-3 py-1 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
            >
              <option value="tier_1_critical">Tier 1 — Critical</option>
              <option value="tier_2_high">Tier 2 — High</option>
              <option value="tier_3_medium">Tier 3 — Medium</option>
              <option value="tier_4_low">Tier 4 — Low</option>
            </select>
          </div>

          {/* Industry */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-industry">Industry</Label>
            <Input
              id="ev-industry"
              value={form.industry}
              onChange={(e) => set('industry', e.target.value)}
              placeholder="e.g. Financial Services"
            />
          </div>

          {/* Website */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-website">Website</Label>
            <Input
              id="ev-website"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          {/* Country */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-country">Headquarters Country</Label>
            <Input
              id="ev-country"
              value={form.country}
              onChange={(e) => set('country', e.target.value)}
              placeholder="e.g. US"
              maxLength={2}
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-description">Description</Label>
            <textarea
              id="ev-description"
              className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-cyan resize-none"
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Brief description of this vendor"
            />
          </div>

          {/* Contract dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ev-start">Contract Start</Label>
              <Input
                id="ev-start"
                type="date"
                value={form.contract_start_date}
                onChange={(e) => set('contract_start_date', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ev-end">Contract End</Label>
              <Input
                id="ev-end"
                type="date"
                value={form.contract_end_date}
                onChange={(e) => set('contract_end_date', e.target.value)}
              />
            </div>
          </div>

          {/* Data Access Level */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-dal">Data Access Level</Label>
            <select
              id="ev-dal"
              value={form.data_access_level}
              onChange={(e) => set('data_access_level', e.target.value)}
              className="flex h-9 w-full rounded border border-border-subtle bg-bg-surface-2 px-3 py-1 text-sm text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
            >
              <option value="none">None</option>
              <option value="public">Public</option>
              <option value="internal">Internal</option>
              <option value="confidential">Confidential</option>
              <option value="restricted">Restricted</option>
            </select>
          </div>

          {/* Services Provided */}
          <div className="space-y-1.5">
            <Label htmlFor="ev-services">Services Provided</Label>
            <Input
              id="ev-services"
              value={form.services_provided}
              onChange={(e) => set('services_provided', e.target.value)}
              placeholder="e.g. Cloud Hosting, Payroll, IT Support (comma-separated)"
            />
          </div>

          <KeyContactsSection vendorId={vendor.id} />

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={update.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending || !form.name.trim()}>
              {update.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

// ── Dual intelligence tiles (Overview reclaimed cell — replaces Key Contacts) ────────────────
// A small state chip on the meta line (9.5px), token trios reused from the cert-card families.
const CHIP_BASE = 'inline-flex items-center rounded border px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide';
function raStateChip(workflow_status: string): { label: string; cls: string } {
  if (workflow_status === 'approved') return { label: 'Approved', cls: 'bg-cert-valid-bg text-cert-valid-fg border-cert-valid-border' };
  if (workflow_status === 'pending_approval') return { label: 'Pending', cls: 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border' };
  return { label: workflow_status.replace(/_/g, ' '), cls: 'bg-cert-neutral-bg text-cert-neutral-fg border-cert-neutral-border' };
}
// The urgency band colour is DERIVED from real days-remaining (expiry − today), not a score.
// Trap #55: fills/borders/text use the cert validity family tokens (Design's deeper band-specific
// tints have no exact palette token — same family, honest urgency).
function expiryBand(days: number | null): { wrap: string; text: string; label: string } {
  if (days === null) return { wrap: 'bg-cert-neutral-bg border-cert-neutral-border', text: 'text-cert-neutral-fg', label: 'no expiry' };
  if (days < 0) return { wrap: 'bg-cert-expired-bg border-cert-expired-border', text: 'text-cert-expired-fg', label: 'days overdue' };
  if (days <= 60) return { wrap: 'bg-cert-warn-bg border-cert-warn-border', text: 'text-cert-warn-fg', label: 'days left' };
  return { wrap: 'bg-cert-valid-bg border-cert-valid-border', text: 'text-cert-valid-fg', label: 'days left' };
}
function ExpiryTile({ icon: Icon, eyebrow, item, emptyText, viewAllLabel, onViewAll }: {
  icon: LucideIcon;
  eyebrow: string;
  item: { name: string; chipLabel: string; chipCls: string; expiry: string | null } | null;
  emptyText: string;
  viewAllLabel: string;
  onViewAll: () => void;
}) {
  const days = item?.expiry ? Math.ceil((Date.parse(item.expiry) - Date.now()) / 86_400_000) : null;
  const band = expiryBand(days);
  return (
    <div className="shrink-0 flex items-stretch overflow-hidden rounded-lg border border-border-subtle bg-bg-surface">
      <div className="flex-1 min-w-0 p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Icon className="h-[15px] w-[15px] shrink-0 text-text-secondary" />
          <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-text-muted">{eyebrow}</span>
        </div>
        <div className="min-h-[44px] flex flex-col justify-center gap-1">
          {item ? (
            <>
              <span className="truncate text-sm font-semibold text-text-primary" title={item.name}>{item.name}</span>
              <div className="flex items-center gap-1.5 min-w-0">
                <span className={`shrink-0 ${CHIP_BASE} ${item.chipCls}`}>{item.chipLabel}</span>
                <span className="truncate text-xs text-text-secondary">{item.expiry ? `Expires ${formatDate(item.expiry)}` : 'No expiry set'}</span>
              </div>
            </>
          ) : (
            <span className="text-[13px] text-text-secondary">{emptyText}</span>
          )}
        </div>
        <button type="button" onClick={onViewAll} className="self-start text-xs font-semibold text-accent-cyan hover:underline">
          {viewAllLabel} →
        </button>
      </div>
      <div className={`shrink-0 w-[86px] flex flex-col items-center justify-center gap-0.5 border-l ${item ? band.wrap : 'bg-bg-base border-border-subtle'}`}>
        {item ? (
          <>
            <span className={`text-[17px] font-bold leading-none tabular-nums ${band.text}`}>{days === null ? '—' : Math.abs(days)}</span>
            <span className={`text-[9px] font-bold uppercase tracking-widest text-center leading-tight opacity-80 ${band.text}`}>{band.label}</span>
          </>
        ) : (
          <span className="text-base font-bold text-text-faint">—</span>
        )}
      </div>
    </div>
  );
}

function OverviewTab({ vendorId, onViewAllAssessments, onViewAllCerts, onViewAllRiskAcceptances }: { vendorId: string; onViewAllAssessments: () => void; onViewAllCerts: () => void; onViewAllRiskAcceptances: () => void }) {
  const { data: vendor } = useVendor(vendorId);
  const { data: assessments = [], isLoading: assessmentsLoading } = useVendorAssessments(vendorId);
  const { data: certs = [] } = useVendorCertifications(vendorId);
  const { data: ras = [] } = useVendorRiskAcceptances(vendorId);
  const navigate = useNavigate();

  const recentAssessments = assessments.slice(0, 3);

  // Tile 1 — next-to-expire ACTIVE certification: validity Active/Expiring-soon with an expiry date,
  // soonest expiry; tie → most recent cert_date. Empty when none active.
  const activeCerts = certs.filter((c) => {
    const v = validityDescriptor(c).label;
    return c.expiry_date && (v === 'Active' || v === 'Expiring soon');
  });
  const certPick = activeCerts.length
    ? [...activeCerts].sort((a, b) =>
        (Date.parse(a.expiry_date!) - Date.parse(b.expiry_date!)) ||
        (Date.parse(b.cert_date ?? '') - Date.parse(a.cert_date ?? '')))[0]
    : null;
  const certItem = certPick
    ? {
        name: friendlyCertType(certPick.cert_type),
        chipLabel: reviewDescriptor(certPick.review_status ?? '').label,
        chipCls: reviewDescriptor(certPick.review_status ?? '').badge,
        expiry: certPick.expiry_date,
      }
    : null;

  // Tile 2 — next-expiring OPEN risk acceptance: state Approved/Pending, soonest acceptance_expires_at;
  // fallback most-recent open by created_at. Empty when none open.
  const openRas = ras.filter((r) => r.workflow_status === 'approved' || r.workflow_status === 'pending_approval');
  const rasWithExpiry = openRas.filter((r) => r.acceptance_expires_at);
  const raPick = rasWithExpiry.length
    ? [...rasWithExpiry].sort((a, b) => Date.parse(a.acceptance_expires_at!) - Date.parse(b.acceptance_expires_at!))[0]
    : openRas.length
      ? [...openRas].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0]
      : null;
  const raItem = raPick
    ? { name: raPick.title, chipLabel: raStateChip(raPick.workflow_status).label, chipCls: raStateChip(raPick.workflow_status).cls, expiry: raPick.acceptance_expires_at }
    : null;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Vendor details */}
      <Card>
        <CardHeader><CardTitle className="text-base">Vendor Details</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {[
            { icon: Building2, label: 'Industry', value: vendor?.industry },
            { icon: MapPin, label: 'Headquarters', value: vendor?.country },
            { icon: Globe, label: 'Website', value: vendor?.website, link: true },
            { icon: Calendar, label: 'Vendor Since', value: vendor?.created_at ? formatDate(vendor.created_at) : null },
            { icon: Calendar, label: 'Contract Start', value: vendor?.contract_start_date ? formatDate(vendor.contract_start_date) : null },
            { icon: Calendar, label: 'Contract End', value: vendor?.contract_end_date ? formatDate(vendor.contract_end_date) : null },
          ].map(({ icon: Icon, label, value, link }) => (
            <div key={label} className="flex items-center gap-3">
              <Icon className="h-4 w-4 shrink-0 text-text-secondary" />
              <span className="text-sm text-text-secondary w-28 shrink-0">{label}</span>
              {value ? (
                link ? (
                  <a href={value} target="_blank" rel="noreferrer" className="text-sm text-accent-cyan hover:underline flex items-center gap-1">
                    {value} <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="text-sm text-text-primary">{value}</span>
                )
              ) : (
                <span className="text-sm text-text-secondary">—</span>
              )}
            </div>
          ))}
          {vendor?.description && (
            <div className="pt-2 border-t border-border-subtle">
              <p className="text-sm text-text-secondary leading-relaxed">{vendor.description}</p>
            </div>
          )}
          {(vendor?.tags ?? []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Tag className="h-3.5 w-3.5 text-text-secondary" />
              {(vendor?.tags ?? []).map((tag) => (
                <Badge key={tag} variant="muted" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Intelligence tiles — cert expiry + risk acceptance (replaces Key Contacts, which mirrored
          the CRM Contacts tab). Two stacked content-height tiles; a spacer keeps them top-aligned
          when the cell stretches to Vendor Details' height. */}
      <div className="flex flex-col gap-3">
        <ExpiryTile
          icon={Award}
          eyebrow="Next certification to expire"
          item={certItem}
          emptyText="No active certifications on file."
          viewAllLabel="View all certifications"
          onViewAll={onViewAllCerts}
        />
        <ExpiryTile
          icon={Shield}
          eyebrow="Next risk response to expire"
          item={raItem}
          emptyText="No open risk responses."
          viewAllLabel="View all risk responses"
          onViewAll={onViewAllRiskAcceptances}
        />
        <div className="flex-1" />
      </div>

      {/* Recent assessments */}
      <Card className="lg:col-span-2">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Recent Assessments</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-accent-cyan"
            onClick={onViewAllAssessments}
          >
            View all assessments <ChevronRight className="h-3 w-3 ml-0.5" />
          </Button>
        </CardHeader>
        <CardContent>
          {assessmentsLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : recentAssessments.length === 0 ? (
            <p className="text-sm text-text-secondary">No assessments yet</p>
          ) : (
            <div className="space-y-2">
              {recentAssessments.map((a) => (
                <div key={a.id} onClick={() => navigate(`/assessments/${a.id}`)} className="flex items-center justify-between rounded-lg border border-border-subtle px-4 py-3 cursor-pointer hover:bg-bg-surface-2 transition-colors">
                  <div>
                    <p className="text-sm font-medium text-text-primary">{a.title ?? 'Assessment'}</p>
                    <p className="text-xs text-text-secondary mt-0.5">
                      {a.issued_at ? `Issued ${formatDate(a.issued_at)}` : '—'}
                      {a.due_date ? ` · Due ${formatDate(a.due_date)}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {a.final_score != null && (
                      <span className="text-sm font-mono text-text-primary">{a.final_score}%</span>
                    )}
                    <StatusBadge status={a.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* D-VENDOR-LIFECYCLE.2-PE-3: Lifecycle History — shown when history exists */}
      <div className="lg:col-span-2">
        <VendorLifecycleHistory vendorId={vendorId} />
      </div>

    </div>
  );
}

// ── Assessments tab ────────────────────────────────────────────────────────────

export function AssessmentsTab({ vendorId, onIssueAssessment }: { vendorId: string; onIssueAssessment?: () => void }) {
  const { data: assessments = [], isLoading } = useVendorAssessments(vendorId);
  const navigate = useNavigate();

  if (isLoading) return <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;

  if (assessments.length === 0) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No assessments"
        description="No assessments have been issued for this vendor yet."
        action={onIssueAssessment ? { label: 'Issue Assessment', onClick: onIssueAssessment } : undefined}
      />
    );
  }

  const active = assessments.filter((a) => ACTIVE_STATUSES.has(a.status));
  const closed = assessments.filter((a) => !ACTIVE_STATUSES.has(a.status));

  const tableHead = (
    <thead className="sticky top-0 bg-bg-surface border-b border-border-subtle">
      <tr>
        {['Title', 'Status', 'Issued', 'Due Date', 'Score', 'Actions'].map((h) => (
          <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">{h}</th>
        ))}
      </tr>
    </thead>
  );

  const renderRows = (rows: typeof assessments, withReopenedBadge: boolean) =>
    rows.map((a) => (
      <tr key={a.id} onClick={() => navigate(`/assessments/${a.id}`)} className="border-b border-border-subtle hover:bg-bg-surface-2 transition-colors cursor-pointer">
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <p className="font-medium text-text-primary">{a.title ?? 'Assessment'}</p>
            {withReopenedBadge && (a.status as string) === 'reopened' && (
              <span className="inline-flex items-center text-[10px] font-medium text-accent-amber bg-accent-amber/10 px-1.5 py-0.5 rounded">Reopened</span>
            )}
          </div>
        </td>
        <td className="px-4 py-3"><AssessmentStatusChip status={a.status} /></td>
        <td className="px-4 py-3 text-text-secondary">{a.issued_at ? formatDate(a.issued_at) : '—'}</td>
        <td className="px-4 py-3 text-text-secondary">{a.due_date ? formatDate(a.due_date) : '—'}</td>
        <td className="px-4 py-3 font-mono text-text-primary">{a.final_score != null ? `${a.final_score}%` : '—'}</td>
        <td className="px-4 py-3">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate(`/assessments/${a.id}`)}>
            View
          </Button>
        </td>
      </tr>
    ));

  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">Active</h3>
        {active.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-4">No active assessments.</p>
        ) : (
          <div className="rounded-lg border border-border-subtle overflow-auto">
            <table className="w-full text-sm">
              {tableHead}
              <tbody>{renderRows(active, false)}</tbody>
            </table>
          </div>
        )}
      </section>
      <section>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">Closed &amp; Historical</h3>
        {closed.length === 0 ? (
          <p className="text-sm text-text-secondary text-center py-4">No historical assessments.</p>
        ) : (
          <div className="rounded-lg border border-border-subtle overflow-auto">
            <table className="w-full text-sm">
              {tableHead}
              <tbody>{renderRows(closed, true)}</tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ── Certifications tab ─────────────────────────────────────────────────────────

type ReviewAction = 'accepted' | 'needs_follow_up' | 'rejected';

const REVIEW_ACTION_MODAL_BODY: Record<ReviewAction, string> = {
  accepted: 'Mark this certification as accepted?',
  needs_follow_up: 'Mark this certification as needing follow-up?',
  rejected: 'Mark this certification as rejected?',
};

// Display-only friendly labels for the cert_type enum (the stored enum value is unchanged).
const CERT_TYPE_LABELS: Record<string, string> = {
  SOC2_TYPE1: 'SOC 2 Type I',
  SOC2_TYPE2: 'SOC 2 Type II',
  ISO27001: 'ISO/IEC 27001',
  HITRUST_CSF: 'HITRUST CSF',
  PCI_DSS_ROC: 'PCI DSS (RoC)',
  FEDRAMP: 'FedRAMP',
  ISO27017: 'ISO/IEC 27017',
  GDPR_ART42: 'GDPR Art. 42',
  CSA_STAR_2: 'CSA STAR Level 2',
};
function friendlyCertType(t: string): string {
  return CERT_TYPE_LABELS[t] ?? t;
}

// ── Cert badge families (design tokens) — two ORTHOGONAL dimensions in DISTINCT families
//    so a validity badge never reads as a review-state badge. Left-labelled rows in the rail.
const BADGE_BASE = 'inline-flex items-center gap-1 text-[11px] font-bold tracking-wide rounded-md border px-2 py-0.5';
const BADGE_VALID   = 'bg-cert-valid-bg text-cert-valid-fg border-cert-valid-border';
const BADGE_WARN    = 'bg-cert-warn-bg text-cert-warn-fg border-cert-warn-border';
const BADGE_EXPIRED = 'bg-cert-expired-bg text-cert-expired-fg border-cert-expired-border';
const BADGE_NEUTRAL = 'bg-cert-neutral-bg text-cert-neutral-fg border-cert-neutral-border';
const BADGE_INFO    = 'bg-cert-info-bg text-cert-info-fg border-cert-info-border';

// Validity (traffic-light): drives both the rail badge AND the 4px left edge stripe.
function validityDescriptor(c: Certification): { label: string; badge: string; stripe: string } {
  if (c.lifecycle_status === 'withdrawn') return { label: 'Withdrawn', badge: BADGE_NEUTRAL, stripe: 'bg-cert-neutral-border' };
  const s = c.derived_status ?? c.lifecycle_status;
  if (s === 'expired') return { label: 'Expired', badge: BADGE_EXPIRED, stripe: 'bg-cert-expired-stripe' };
  if (s === 'expiring_soon') return { label: 'Expiring soon', badge: BADGE_WARN, stripe: 'bg-cert-warn-stripe' };
  return { label: 'Active', badge: BADGE_VALID, stripe: 'bg-cert-valid-stripe' };
}

// Review-state (distinct family): neutral / info-sky (follow-up, NOT amber) / green / red.
function reviewDescriptor(status: string): { label: string; badge: string } {
  switch (status) {
    case 'accepted': return { label: 'Accepted', badge: BADGE_VALID };
    case 'rejected': return { label: 'Rejected', badge: BADGE_EXPIRED };
    case 'needs_follow_up': return { label: 'Needs follow-up', badge: BADGE_INFO };
    default: return { label: 'Not reviewed', badge: BADGE_NEUTRAL };
  }
}

// Coverage window (V987) → a readable range; "Not captured" until vendor entry (Sprint 3).
function coverageLabel(start: string | null | undefined, end: string | null | undefined): string {
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
  if (start) return `From ${formatDate(start)}`;
  if (end) return `Through ${formatDate(end)}`;
  return 'Not captured';
}

// Expiration + a "· N days" hint when expiring soon; colored to match validity.
function daysUntil(dateStr: string): number | null {
  const t = Date.parse(dateStr);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.ceil((t - Date.now()) / 86_400_000));
}
function expirationDescriptor(c: Certification): { text: string; cls: string } {
  if (!c.expiry_date) return { text: '—', cls: 'text-text-primary' };
  const d = formatDate(c.expiry_date);
  const s = c.derived_status ?? c.lifecycle_status;
  if (s === 'expired') return { text: d, cls: 'text-cert-expired-fg font-semibold' };
  if (s === 'expiring_soon') {
    const days = daysUntil(c.expiry_date);
    return { text: days != null ? `${d} · ${days} days` : d, cls: 'text-cert-warn-fg font-semibold' };
  }
  return { text: d, cls: 'text-text-primary' };
}

export function CertificationsTab({ vendorId }: { vendorId: string }) {
  const { data: certs = [], isLoading } = useVendorCertifications(vendorId);
  const reviewMutation = useReviewCertification(vendorId);
  const [reviewTarget, setReviewTarget] = useState<{ certId: string; action: ReviewAction } | null>(null);
  const [reason, setReason] = useState('');
  // Which decided cert is currently in "change decision" mode (its action buttons re-shown).
  const [changingId, setChangingId] = useState<string | null>(null);

  function openReview(certId: string, action: ReviewAction) {
    setReviewTarget({ certId, action });
    setReason('');
  }

  function closeReview() {
    setReviewTarget(null);
    setReason('');
    setChangingId(null);
  }

  async function handleConfirm() {
    if (!reviewTarget) return;
    try {
      await reviewMutation.mutateAsync({
        certId: reviewTarget.certId,
        action: reviewTarget.action,
        reason: reviewTarget.action === 'needs_follow_up' ? reason : null,
      });
      closeReview();
    } catch {
      // onError toast handled in hook; modal stays open for retry
    }
  }

  const confirmDisabled =
    reviewMutation.isPending ||
    (reviewTarget?.action === 'needs_follow_up' && reason.trim().length === 0);

  if (isLoading) return <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  if (certs.length === 0) {
    return (
      <EmptyState
        icon={Award}
        title="No certifications"
        description="No certifications have been uploaded for this vendor."
      />
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3.5">
        {certs.map((c) => {
          const isArchived = c.archived_because != null;
          const reviewStatus = c.review_status ?? 'not_reviewed';
          const decided = reviewStatus !== 'not_reviewed';
          const validity = validityDescriptor(c);
          const review = reviewDescriptor(reviewStatus);
          const exp = expirationDescriptor(c);
          const coverageNull = c.coverage_start == null && c.coverage_end == null;
          const showActions = !isArchived && (!decided || changingId === c.id);

          return (
            <div key={c.id} className="flex overflow-hidden rounded-lg border border-border-subtle bg-bg-surface">
              {/* 4px validity edge stripe (traffic-light) */}
              <div className={`w-1 shrink-0 ${validity.stripe}`} aria-hidden="true" />

              {/* Inner: metadata over rail on mobile; metadata | rail on md+ */}
              <div className="flex-1 min-w-0 flex flex-col md:flex-row">
                {/* ── Metadata region ── */}
                <div className="flex-1 min-w-0 p-5 flex flex-col gap-4">
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 shrink-0 rounded-lg bg-bg-surface-2 border border-border-subtle flex items-center justify-center">
                      <Award className="h-4 w-4 text-accent-amber" />
                    </div>
                    <div className="min-w-0 flex flex-col gap-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold text-text-primary">{friendlyCertType(c.cert_type)}</span>
                        {c.cert_standard_version && (
                          <span className="text-[10px] uppercase tracking-wide text-text-muted border border-border-subtle rounded px-1.5 py-0.5">
                            {c.cert_standard_version}
                          </span>
                        )}
                      </div>
                      <span className="text-[13px] text-text-muted">{c.issuing_body ?? '—'}</span>
                    </div>
                  </div>

                  {/* Metadata grid */}
                  <dl className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-x-5 gap-y-3.5">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <dt className="text-[10.5px] font-bold uppercase tracking-wider text-text-secondary">Certification date</dt>
                      <dd className="text-[13.5px] text-text-primary">{c.cert_date ? formatDate(c.cert_date) : '—'}</dd>
                    </div>
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <dt className="text-[10.5px] font-bold uppercase tracking-wider text-text-secondary">Coverage period</dt>
                      <dd className={`text-[13.5px] ${coverageNull ? 'text-text-faint italic' : 'text-text-primary'}`}>
                        {coverageLabel(c.coverage_start, c.coverage_end)}
                      </dd>
                    </div>
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <dt className="text-[10.5px] font-bold uppercase tracking-wider text-text-secondary">Expiration</dt>
                      <dd className={`text-[13.5px] ${exp.cls}`}>{exp.text}</dd>
                    </div>
                  </dl>

                  {/* Scope — its own full-width row */}
                  <div className="flex flex-col gap-1 pt-3 border-t border-border-subtle">
                    <span className="text-[10.5px] font-bold uppercase tracking-wider text-text-secondary">Scope</span>
                    <span className={`text-[13.5px] leading-relaxed ${c.scope_description ? 'text-text-primary' : 'text-text-faint italic'}`}>
                      {c.scope_description || 'Not captured'}
                    </span>
                  </div>

                  {/* Out-of-band document guidance (no upload/download/preview — metadata only) */}
                  <p className="text-xs text-text-secondary leading-relaxed">
                    Certification documents are exchanged via an approved secure method outside VeriGRC — the platform captures metadata, not the documents themselves.
                  </p>
                </div>

                {/* ── Review rail (consolidated status + actions) ── */}
                <div className="md:flex-[0_0_268px] md:min-w-[240px] bg-bg-surface-2 border-t md:border-t-0 md:border-l border-border-subtle p-4 flex flex-col gap-3.5">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-text-muted">Review</span>

                  {/* Two labelled rows keep the orthogonal dimensions separate */}
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between gap-2.5">
                      <span className="text-xs text-text-secondary">Validity</span>
                      <span className={`${BADGE_BASE} ${validity.badge}`}>{validity.label}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2.5">
                      <span className="text-xs text-text-secondary">Review state</span>
                      <span className={`${BADGE_BASE} ${review.badge}`}>{review.label}</span>
                    </div>
                  </div>

                  <div className="h-px bg-border-subtle" />

                  {/* Review note + author·date (author·date from the joined reviewer + reviewed_at) */}
                  {c.review_notes && (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10.5px] font-bold uppercase tracking-wider text-text-secondary">Review note</span>
                      <span className="text-[12.5px] text-text-primary leading-relaxed break-words">{c.review_notes}</span>
                      {(c.reviewer_name || c.reviewed_at) && (
                        <span className="text-[11.5px] text-text-secondary">
                          {[c.reviewer_name, c.reviewed_at ? formatDate(c.reviewed_at) : null].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  {isArchived ? (
                    <p className="text-[11.5px] text-text-secondary">Vendor withdrew this certification.</p>
                  ) : showActions ? (
                    <div className="flex flex-col gap-2">
                      <Button size="sm" className="w-full h-8 text-[13px]" onClick={() => openReview(c.id, 'accepted')}>
                        Accept
                      </Button>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => openReview(c.id, 'needs_follow_up')}>
                          Follow-up
                        </Button>
                        <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={() => openReview(c.id, 'rejected')}>
                          Reject
                        </Button>
                      </div>
                      {decided && (
                        <button
                          type="button"
                          className="self-start text-[11.5px] text-text-secondary hover:text-text-primary transition-colors"
                          onClick={() => setChangingId(null)}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" className="w-full h-8 text-xs" onClick={() => setChangingId(c.id)}>
                      Change decision
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Review confirmation modal */}
      <Dialog open={reviewTarget !== null} onOpenChange={(o) => { if (!o) closeReview(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review certification</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <p className="text-sm text-text-primary">
              {reviewTarget ? REVIEW_ACTION_MODAL_BODY[reviewTarget.action] : ''}
            </p>
            {reviewTarget?.action === 'needs_follow_up' && (
              <div className="space-y-1.5">
                <Label htmlFor="review-reason">Reason (required)</Label>
                <Textarea
                  id="review-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Describe what additional information or clarification you need from the vendor"
                  maxLength={1000}
                  rows={4}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeReview} disabled={reviewMutation.isPending}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={confirmDisabled}>
              {reviewMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Risk Acceptances tab ───────────────────────────────────────────────────────
//
// §3.8 / AC-RA3b-28 / AC-RA3b-29: action menu for PENDING_APPROVAL rows
// when current user is Approver-class (risk_manager | super_admin).

function RiskAcceptancesTab({ vendorId }: { vendorId: string }) {
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuthStore();
  const isApproverClass = hasAnyRole(['risk_manager', 'super_admin']);
  // RA.4: current user ID for Revoke 4-eyes pre-flight (D-RA.4-PE-1)
  const currentUserId = user?.id ?? null;

  const { data: acceptances = [], isLoading } = useVendorRiskAcceptances(vendorId);

  // Action modal state — RA.4: 5 types; RA.5: extended to 7 (approve-renewal + deny-renewal)
  // D-RA.9-PE-1: entry narrowed to minimal shape (RiskAcceptanceActionModal uses id + title only)
  const [actionModal, setActionModal] = useState<{
    action: 'approve' | 'deny' | 'return' | 'close-as-mitigated' | 'revoke' | 'approve-renewal' | 'deny-renewal';
    entry: { id: string; title: string | null };
  } | null>(null);

  if (isLoading) return <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>;
  if (acceptances.length === 0) {
    return (
      <EmptyState
        icon={Shield}
        title="No risk responses"
        description="No risk responses have been recorded for this vendor."
      />
    );
  }

  return (
    <>
      <div className="space-y-3">
        {acceptances.map((r) => {
          // RA.4: APPROVED also shows action menu (AC-RA4-25 / AC-RA3b-28)
          const showActionMenu = isApproverClass && (
            r.workflow_status === 'pending_approval' || r.workflow_status === 'approved'
          );
          // D-RA.9-PE-1: pass minimal shape directly (no cast needed; RiskAcceptanceActionModal entry prop narrowed)

          return (
            <div
              key={r.id}
              className="flex items-center justify-between rounded-lg border border-border-subtle px-4 py-3 hover:bg-bg-surface-2 transition-colors cursor-pointer"
              onClick={() => navigate(`/risk-acceptances/${r.id}`)}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-text-primary truncate">{r.title}</p>
                {r.description && <p className="text-xs text-text-secondary mt-0.5 line-clamp-1">{r.description}</p>}
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-4">
                <StatusBadge status={rrBadgeStatus(r.workflow_status, r.closure_reason)} />
                {r.acceptance_expires_at && (
                  <span className="text-xs text-text-secondary">{formatDate(r.acceptance_expires_at)}</span>
                )}
                {/* AC-RA3b-28: action menu for PENDING_APPROVAL + Approver */}
                {showActionMenu && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {r.workflow_status === 'pending_approval' ? (
                        r.parent_acceptance_id !== null ? (
                          /* RA.5: child renewal entry — Approve Renewal + Deny Renewal */
                          <>
                            <DropdownMenuItem
                              disabled={currentUserId === r.submitted_by_user_id}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (currentUserId !== r.submitted_by_user_id) {
                                  setActionModal({ action: 'approve-renewal', entry: { id: r.id, title: r.title } });
                                }
                              }}
                            >
                              <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                              Approve Renewal
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setActionModal({ action: 'deny-renewal', entry: { id: r.id, title: r.title } });
                              }}
                              className="text-accent-rose"
                            >
                              <XCircle className="mr-2 h-3.5 w-3.5" />
                              Deny Renewal
                            </DropdownMenuItem>
                          </>
                        ) : (
                          /* Standard pending approval — Approve + Deny + Return */
                          <>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setActionModal({ action: 'approve', entry: { id: r.id, title: r.title } });
                              }}
                            >
                              <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                              Approve
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setActionModal({ action: 'deny', entry: { id: r.id, title: r.title } });
                              }}
                              className="text-accent-rose"
                            >
                              <XCircle className="mr-2 h-3.5 w-3.5" />
                              Deny
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setActionModal({ action: 'return', entry: { id: r.id, title: r.title } });
                              }}
                            >
                              <RotateCcw className="mr-2 h-3.5 w-3.5" />
                              Return for Info
                            </DropdownMenuItem>
                          </>
                        )
                      ) : (
                        /* r.state === 'approved' — RA.4 actions + RA.5 renewal navigation */
                        <>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setActionModal({ action: 'close-as-mitigated', entry: { id: r.id, title: r.title } });
                            }}
                          >
                            <CheckCircle className="mr-2 h-3.5 w-3.5 text-accent-emerald" />
                            Close as Mitigated
                          </DropdownMenuItem>
                          {/* Revoke — 4-eyes pre-flight per D-RA.4-PE-1 */}
                          <DropdownMenuItem
                            disabled={currentUserId === r.submitted_by_user_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (currentUserId !== r.submitted_by_user_id) {
                                setActionModal({ action: 'revoke', entry: { id: r.id, title: r.title } });
                              }
                            }}
                            className="text-accent-rose"
                          >
                            <XCircle className="mr-2 h-3.5 w-3.5" />
                            Revoke
                          </DropdownMenuItem>
                          {/* RA.5: navigate to detail page where renewal_window_open is computed */}
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/risk-acceptances/${r.id}`);
                            }}
                          >
                            <RefreshCw className="mr-2 h-3.5 w-3.5 text-accent-cyan" />
                            Request Renewal…
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Action modal — AC-RA3b-28 */}
      {actionModal && (
        <RiskAcceptanceActionModal
          action={actionModal.action}
          entry={actionModal.entry}
          mutationHook={useRiskAcceptanceAction as any}
          onSuccess={() => setActionModal(null)}
          onClose={() => setActionModal(null)}
        />
      )}
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasAnyRole } = useAuthStore();
  const canManage = hasAnyRole(['risk_manager', 'ciso', 'super_admin']);
  const { data: vendor, isLoading, error } = useVendor(id);
  const { data: lifecycleEvents = [] } = useVendorLifecycleEvents(id);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [IssueModal, setIssueModal] = useState<React.ComponentType<{ open: boolean; onClose: () => void; vendorId?: string; vendorName?: string }> | null>(null);

  const openIssueModal = useCallback(async () => {
    if (!IssueModal) {
      const mod = await import('@/features/assessments/IssueAssessmentModal');
      setIssueModal(() => mod.IssueAssessmentModal);
    }
    setIssueModalOpen(true);
  }, [IssueModal]);

  // D-VENDOR-LIFECYCLE.2: retire/reactivate eligibility per VL.1 state machine
  const canRetire = canManage && !!vendor && ['active', 'under_review', 'suspended'].includes(vendor.status);
  const canReactivate = canManage && vendor?.status === 'offboarded';
  const canEdit = canManage && vendor?.status !== 'offboarded';
  const canIssueAssessment = canManage && vendor?.status !== 'offboarded';

  // For offboarded banner: most recent retirement event (D-VENDOR-LIFECYCLE.2-PE-2)
  const lastRetiredEvent = lifecycleEvents.find((e) => e.event_type === 'vendor.retired') ?? null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !vendor) {
    return (
      <EmptyState
        icon={Building2}
        title="Vendor not found"
        description="This vendor may have been removed or you don't have access."
        action={{ label: 'Back to vendors', onClick: () => navigate('/vendors') }}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-cyan-dim text-base font-bold text-accent-cyan">
              {vendor.name.charAt(0).toUpperCase()}
            </div>
            <h1 className="text-2xl font-bold text-text-primary">{vendor.name}</h1>
            <StatusBadge status={vendor.status} />
            <Badge variant="muted">{TIER_LABELS[vendor.tier] ?? vendor.tier}</Badge>
          </div>
          {vendor.website && (
            <a
              href={vendor.website}
              target="_blank"
              rel="noreferrer"
              className="mt-2 flex items-center gap-1 text-sm text-accent-cyan hover:underline"
            >
              <Globe className="h-3.5 w-3.5" />
              {vendor.website}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          )}
          {canIssueAssessment && (
            <Button variant="outline" size="sm" onClick={openIssueModal}>
              <ClipboardList className="h-4 w-4" />
              Issue Assessment
            </Button>
          )}
          {/* D-VENDOR-LIFECYCLE.2-PE-1: inline Dialog lifecycle actions */}
          {canRetire && (
            <Button variant="outline" size="sm" onClick={() => setRetireOpen(true)}>
              <Archive className="h-4 w-4" />
              Retire
            </Button>
          )}
          {canReactivate && (
            <Button variant="outline" size="sm" onClick={() => setReactivateOpen(true)}>
              <RefreshCw className="h-4 w-4" />
              Reactivate
            </Button>
          )}
        </div>
      </div>

      {/* D-VENDOR-LIFECYCLE.2-PE-2: offboarded banner */}
      {vendor.status === 'offboarded' && (
        <div className="flex items-center gap-3 rounded-lg border border-border-subtle bg-bg-surface px-4 py-3">
          <History className="h-4 w-4 text-text-secondary shrink-0" />
          <p className="text-sm text-text-secondary">
            This vendor has been retired
            {lastRetiredEvent ? ` on ${formatDate(lastRetiredEvent.occurred_at)}` : ''}.
            {' '}See the Lifecycle History in the Overview tab for full details.
          </p>
        </div>
      )}

      {/* Vendor BI header (assessment-viz Surface 2) — replaces the removed composite risk score */}
      <VendorBiHeader vendor={vendor} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="assessments">Assessments</TabsTrigger>
          <TabsTrigger value="certifications">Certifications</TabsTrigger>
          <TabsTrigger value="risk-acceptances">Risk Responses</TabsTrigger>
          <TabsTrigger value="contacts">CRM Contacts</TabsTrigger>
          <TabsTrigger value="portal-users">Vendor Contacts</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab
            vendorId={vendor.id}
            onViewAllAssessments={() => setActiveTab('assessments')}
            onViewAllCerts={() => setActiveTab('certifications')}
            onViewAllRiskAcceptances={() => setActiveTab('risk-acceptances')}
          />
        </TabsContent>

        <TabsContent value="assessments" className="mt-4">
          <AssessmentsTab vendorId={vendor.id} onIssueAssessment={canIssueAssessment ? openIssueModal : undefined} />
        </TabsContent>

        <TabsContent value="certifications" className="mt-4">
          <CertificationsTab vendorId={vendor.id} />
        </TabsContent>

        <TabsContent value="risk-acceptances" className="mt-4">
          <RiskAcceptancesTab vendorId={vendor.id} />
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <CrmContactsTab vendorId={vendor.id} />
        </TabsContent>

        <TabsContent value="portal-users" className="mt-4">
          <PortalUsersTab vendorId={vendor.id} />
        </TabsContent>
      </Tabs>

      {IssueModal && (
        <IssueModal
          open={issueModalOpen}
          onClose={() => setIssueModalOpen(false)}
          vendorId={vendor.id}
          vendorName={vendor.name}
        />
      )}

      <EditVendorModal
        vendor={vendor}
        open={editOpen}
        onClose={() => setEditOpen(false)}
      />

      {/* D-VENDOR-LIFECYCLE.2-PE-1: lifecycle action modals */}
      <VendorRetireModal
        vendorId={vendor.id}
        vendorName={vendor.name}
        open={retireOpen}
        onClose={() => setRetireOpen(false)}
      />
      <VendorReactivateModal
        vendorId={vendor.id}
        vendorName={vendor.name}
        open={reactivateOpen}
        onClose={() => setReactivateOpen(false)}
      />
    </div>
  );
}
